import React, { useState, useEffect, useCallback, useRef } from "react";
import { cn, debounce } from "@/lib/utils";
import { useAudioRecorder, useAudioRecorderStore } from "@/hooks/useAudioRecorder";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { useAppStore } from "@/hooks/useAppStore";
import { Loader2 } from "lucide-react"; // Spinner icon

const MIC_DEBOUNCE_MS = 150; // Prevent rapid taps
const VAD_BTN_NUM_BARS = 5;

interface MicButtonProps {
  onToggleRecording?: () => void;
  isRecording: boolean;
  waveBarRefs: React.RefObject<any>[];
}

//
//
export const MicButton: React.FC<MicButtonProps> = ({
  onToggleRecording: externalToggleRecording,
  isRecording,
  waveBarRefs,
}) => {
  const { isInitializing } = useAudioRecorder(); // Keep this for loading state
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const isVADEnabled = useAppStore((state) => state.isVADEnabled);
  const isVADListening = useAppStore((state) => state.isVADListening);
  const toggleVAD = useAppStore((state) => state.toggleVAD);

  // Internal wave bars for the button's own visualization (using the passed refs)
  // Note: The button uses the first 5 refs from the shared pool
  const displayBars = waveBarRefs ? waveBarRefs.slice(0, VAD_BTN_NUM_BARS) : [];

  const hintText = isVADEnabled
    ? isVADListening
      ? preferredLanguage === "ja"
        ? "音声検出中..."
        : "Listening..."
      : preferredLanguage === "ja"
        ? "自動音声検出中"
        : "Auto Voice Detection"
    : isRecording
      ? preferredLanguage === "ja"
        ? "タップして停止"
        : "Tap to Stop"
      : preferredLanguage === "ja"
        ? "タップして話す"
        : "Tap to Talk";

  return (
    <button
      id="mic-button"
      onClick={async () => {
        const { initialized, initializeRecorder } = useAudioRecorderStore.getState();
        if (!initialized) {
          await initializeRecorder();
        }
        if (isVADEnabled) {
          toggleVAD();
        } else {
          (externalToggleRecording || (() => { }))();
        }
      }}
      disabled={isInitializing}
      style={{
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        background: (isRecording || isVADEnabled) ? '#ff4444' : '#000',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isInitializing ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        opacity: isInitializing ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isInitializing && !isRecording) {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
      }}
      onMouseDown={(e) => {
        if (!isInitializing) {
          e.currentTarget.style.transform = 'scale(0.95)';
        }
      }}
      onMouseUp={(e) => {
        if (!isInitializing) {
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
      aria-label={isVADEnabled ? "Stop Voice Detection" : isRecording ? "Stop recording" : "Start recording"}
    >
      {isInitializing ? (
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      ) : isVADEnabled ? (
        // Hide default icon in VAD mode to show wavelength
        // BUT we need to render the bars for the animation if VAD is active and NOT recording
        // If recording is active, the overlay uses the refs, so we shouldn't steal them.
        !isRecording ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              gap: '3px',
              height: '24px',
            }}
          >
            {displayBars.length > 0 ? displayBars.map((ref, index) => (
              <div
                key={index}
                ref={ref}
                style={{ width: '3px', height: '12px', background: 'white', borderRadius: '2px' }}
              />
            )) : (
              // Fallback if refs aren't ready yet (should rarely happen if initialized)
              <div style={{ width: '100%', height: '100%', background: 'transparent' }} />
            )}
          </div>
        ) : null
      ) : (
        // Static Icon State (Idle or Recording covered by overlay)
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: '3px',
            height: '24px',
          }}
        >
          <div style={{ width: '3px', height: '12px', background: 'white', borderRadius: '2px' }} />
          <div style={{ width: '3px', height: '20px', background: 'white', borderRadius: '2px' }} />
          <div style={{ width: '3px', height: '24px', background: 'white', borderRadius: '2px' }} />
          <div style={{ width: '3px', height: '20px', background: 'white', borderRadius: '2px' }} />
          <div style={{ width: '3px', height: '12px', background: 'white', borderRadius: '2px' }} />
        </div>
      )}
    </button>
  );
};

// Export the recording state and handlers for use in parent component
export const useMicButtonState = (options?: { enableAnimation?: boolean }) => {
  const { isRecording, startRecording, stopRecording, cancelRecording } = useAudioRecorder();
  const { stopAllAudio } = useAudioPlayer();
  const isInitializing = useAudioRecorderStore((state) => state.isInitializing);
  const waveBarRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([]);
  const waveAnimationRef = useRef<number>(0);
  const analyserNode = useAudioRecorderStore((state) => state.analyserNode);
  const [timerLabel, setTimerLabel] = useState("");

  const enableAnimation = options?.enableAnimation ?? true;

  useEffect(() => {
    if (!enableAnimation) {
      return;
    }
    if (isInitializing) {
      if (!waveBarRefs.current.length) {
        waveBarRefs.current = Array(VAD_BTN_NUM_BARS)
          .fill(null)
          .map((_) => React.createRef());
      }
      return;
    }
    if (analyserNode && waveBarRefs.current.length != analyserNode.frequencyBinCount) {
      for (let i = waveBarRefs.current.length; i < analyserNode.frequencyBinCount; i++) {
        waveBarRefs.current.push(React.createRef());
      }
    }
  }, [isInitializing, analyserNode, enableAnimation]);

  const animateWave = useCallback(() => {
    if (!enableAnimation) return;
    const { analyserNode } = useAudioRecorderStore.getState();
    if (isInitializing) return;
    if (!waveBarRefs.current.length) return;
    if (!analyserNode) return;
    const frequencyData = new Uint8Array(analyserNode.frequencyBinCount);
    analyserNode.getByteFrequencyData(frequencyData);
    waveBarRefs.current.forEach((ref, index) => {
      if (ref && ref.current) {
        ref.current.style.height = `${~~(frequencyData[index] / 2 + 1)}%`;
      }
    });
    waveAnimationRef.current = requestAnimationFrame(animateWave);
  }, [isInitializing, analyserNode, enableAnimation]); // Added analyserNode dependency

  const stopWaveAnimation = () => {
    if (waveAnimationRef.current === 0) return;
    cancelAnimationFrame(waveAnimationRef.current);
    waveAnimationRef.current = 0;
  };

  const handleToggleRecording = useCallback(
    debounce(async () => {
      if (isInitializing) return;

      const { initialized, initializeRecorder } = useAudioRecorderStore.getState();
      if (!initialized) {
        await initializeRecorder();
      }

      if (isRecording) {
        console.log('Stopping recording');
        stopRecording();
        // Animation stop handled by effect
      } else {
        console.log('Starting recording');
        stopAllAudio();
        startRecording();
        // Animation start handled by effect
      }
    }, MIC_DEBOUNCE_MS),
    [isRecording, isInitializing, startRecording, stopRecording, stopAllAudio]
  );

  useEffect(() => {
    if (isRecording) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const seconds = Math.floor(elapsed / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        setTimerLabel(`${minutes}:${remainingSeconds.toString().padStart(2, '0')}`);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setTimerLabel("");
    }
  }, [isRecording]);

  // Unified animation effect
  const isVADEnabled = useAppStore((state) => state.isVADEnabled);
  useEffect(() => {
    if (!enableAnimation) return;

    // Determine if we should be animating
    // We animate if:
    // 1. Manually Recording (isRecording = true)
    // 2. VAD Enabled (isVADEnabled = true) - regardless of recording state, though usually mutually exclusive or handled above
    const shouldAnimate = isRecording || isVADEnabled;

    if (shouldAnimate && analyserNode && !isInitializing) {
      console.log('Starting wave animation loop');
      animateWave();
    }

    return () => {
      console.log('Stopping wave animation loop');
      stopWaveAnimation();
    };
  }, [isVADEnabled, isRecording, animateWave, analyserNode, isInitializing, enableAnimation]);

  return {
    isRecording,
    waveBarRefs: waveBarRefs.current,
    timerLabel,
    handleToggleRecording,
    cancelRecording,
  };
};
